/**
 * SchemaForm — render a JSON-schema (as produced by zod-to-json-schema)
 * into a React form. Used by the pipeline-detail Trigger modal so users
 * can supply workflow inputs interactively from the web; the same
 * `{ input: {...} }` is sent to the unified
 * POST /projects/:id/workflows/:type/trigger endpoint that the CLI and
 * cloud sub-graph dispatches use.
 *
 * Scope (intentional — keeps it ~120 lines):
 *   primitives:   string, number/integer, boolean, string-enum (select)
 *   collections:  array of string (comma-separated tag input)
 *   nested:       object (1 level deep) → grouped fieldset
 *   fallback:     anything weirder (nested arrays, oneOf, $ref, …) →
 *                 single "Advanced (JSON)" textarea on the field
 *
 * The server (workflow-input-validator + Zod inside the runner) is the
 * authoritative validator. This component does the bare minimum on the
 * client (required check) so users don't waste a network round-trip on
 * obviously-empty submits.
 *
 * Reports state to the parent via onChange(value, { ok, errors }).
 *   value  — plain JS object suitable to put under { input: <value> }
 *   ok     — false if any required field is empty or any "Advanced JSON"
 *            textarea has unparseable JSON; true otherwise
 *   errors — { fieldPath: 'human message' } for inline rendering
 *
 * Server validation errors (400 from POST /trigger) are passed back in
 * via `serverErrors` prop and rendered alongside client errors. Server
 * field paths use dot notation matching the schema; we render them on
 * the matching field via the same `errors` shape.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './SchemaForm.css';

const PRIMITIVE_TYPES = new Set(['string', 'number', 'integer', 'boolean']);

function isPrimitive(propSchema) {
  return PRIMITIVE_TYPES.has(propSchema?.type);
}

function isStringEnum(propSchema) {
  return propSchema?.type === 'string' && Array.isArray(propSchema.enum) && propSchema.enum.length > 0;
}

function isStringArray(propSchema) {
  return propSchema?.type === 'array' && propSchema.items?.type === 'string';
}

function isSimpleObject(propSchema, depth) {
  if (propSchema?.type !== 'object' || !propSchema.properties) return false;
  if (depth >= 1) return false; // refuse to nest deeper than once
  // Every nested prop must itself be a primitive — otherwise fall through to JSON textarea.
  return Object.values(propSchema.properties).every((p) => isPrimitive(p) || isStringEnum(p) || isStringArray(p));
}

/**
 * Coerce a value to the schema's declared type so the server doesn't
 * have to. Number inputs deliver strings; checkboxes deliver booleans;
 * tag fields deliver arrays. Empty optional fields are dropped from the
 * payload entirely (so the server's `.default()` clauses kick in).
 */
function coerce(propSchema, raw) {
  if (raw === undefined || raw === '' || raw === null) return undefined;
  if (propSchema.type === 'integer') {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? raw : n;
  }
  if (propSchema.type === 'number') {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (propSchema.type === 'boolean') return Boolean(raw);
  if (isStringArray(propSchema)) {
    if (Array.isArray(raw)) return raw;
    return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
  }
  return raw;
}

/**
 * Convert a schema property key into a human-readable label when the
 * template author didn't supply an explicit `title`.
 *
 *   sinceMinutes      → "Since minutes"
 *   slack_channel     → "Slack channel"
 *   max_issues_count  → "Max issues count"
 *   notifyWorker      → "Notify worker"
 *
 * Strategy: split on camelCase / snake_case / kebab-case boundaries,
 * lowercase all but the first word. Templates that want a different
 * label should set `.title()` (custom zod extension) or `.describe(...)`
 * with a leading short label.
 */
function humanizeFieldName(name) {
  return name
    .replace(/[_-]+/g, ' ')                       // snake_case / kebab-case → spaces
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')       // camelCase → spaces
    .replace(/\s+/g, ' ')                          // collapse runs of whitespace
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());      // capitalize first letter only
}

function FieldLabel({ name, schema, required }) {
  const label = schema.title || humanizeFieldName(name);
  return (
    <label className="schema-form-label" htmlFor={`schema-field-${name}`}>
      {label}
      {required && <span className="schema-form-required" aria-label="required">*</span>}
      {schema.description && (
        <span className="schema-form-help">{schema.description}</span>
      )}
    </label>
  );
}

export function PrimitiveField({ name, schema, value, onChange, required, error, disabled }) {
  const id = `schema-field-${name}`;
  const common = { id, name, disabled, 'aria-invalid': Boolean(error), 'aria-required': required };

  if (isStringEnum(schema)) {
    return (
      <select
        {...common}
        className="schema-form-input"
        value={value ?? schema.default ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled={required}>{required ? 'Select…' : '— none —'}</option>
        {schema.enum.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (schema.type === 'boolean') {
    return (
      <input
        {...common}
        className="schema-form-checkbox"
        type="checkbox"
        checked={Boolean(value ?? schema.default ?? false)}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }

  if (schema.type === 'integer' || schema.type === 'number') {
    return (
      <input
        {...common}
        className="schema-form-input"
        type="number"
        step={schema.type === 'integer' ? 1 : 'any'}
        min={schema.minimum}
        max={schema.maximum}
        value={value ?? schema.default ?? ''}
        placeholder={schema.default !== undefined ? `default: ${schema.default}` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // string (multiline if it's "obviously long" — heuristic on description
  // length or `format: 'multiline'`; defensible default for most cases).
  const isMultiline = schema.format === 'multiline'
    || (schema.description && schema.description.length > 80)
    || (schema.maxLength && schema.maxLength > 200);

  if (isMultiline) {
    return (
      <textarea
        {...common}
        className="schema-form-textarea"
        rows={4}
        value={value ?? schema.default ?? ''}
        placeholder={schema.examples?.[0] || schema.default || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <input
      {...common}
      className="schema-form-input"
      type="text"
      value={value ?? schema.default ?? ''}
      placeholder={schema.examples?.[0] || schema.default || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function StringArrayField({ name, schema, value, onChange, required, error, disabled }) {
  const id = `schema-field-${name}`;
  const display = Array.isArray(value) ? value.join(', ') : (value ?? '');
  return (
    <input
      id={id}
      className="schema-form-input"
      type="text"
      disabled={disabled}
      aria-invalid={Boolean(error)}
      aria-required={required}
      value={display}
      placeholder={schema.examples?.[0] || 'comma-separated values'}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function JsonField({ name, value, onChange, disabled, error }) {
  const id = `schema-field-${name}`;
  return (
    <textarea
      id={id}
      className="schema-form-textarea schema-form-textarea-mono"
      rows={4}
      disabled={disabled}
      aria-invalid={Boolean(error)}
      value={typeof value === 'string' ? value : JSON.stringify(value ?? null, null, 2)}
      placeholder='{"key": "value"}'
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function SchemaForm({
  schema,
  value = {},
  onChange,
  disabled = false,
  serverErrors = {},
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value || {}); }, [value]);

  // Memoize so evaluate()'s useCallback dependency array stays stable
  // across re-renders that don't actually change the schema.
  const properties = useMemo(() => schema?.properties || {}, [schema]);
  const required = useMemo(() => new Set(schema?.required || []), [schema]);
  const propNames = useMemo(() => Object.keys(properties), [properties]);

  const evaluate = useCallback((next) => {
    const errors = {};
    const payload = {};

    for (const name of propNames) {
      const propSchema = properties[name];
      const raw = next[name];

      // Object / fallback fields stored as parsed object OR raw string.
      // Strings get a "tried to parse" check here.
      if (!isPrimitive(propSchema) && !isStringArray(propSchema) && !isSimpleObject(propSchema, 0)) {
        if (typeof raw === 'string' && raw.trim()) {
          try {
            payload[name] = JSON.parse(raw);
          } catch (e) {
            errors[name] = 'Invalid JSON';
          }
        } else if (raw && typeof raw === 'object') {
          payload[name] = raw;
        } else if (required.has(name)) {
          errors[name] = 'Required';
        }
        continue;
      }

      const coerced = coerce(propSchema, raw);
      if (coerced === undefined) {
        if (required.has(name)) errors[name] = 'Required';
        continue;
      }
      payload[name] = coerced;
    }

    // Server-side errors layered on top — these win because the server
    // is authoritative; client checks are pre-flight only.
    for (const [k, v] of Object.entries(serverErrors || {})) errors[k] = v;

    onChange(payload, { ok: Object.keys(errors).length === 0, errors });
  }, [propNames, properties, required, serverErrors, onChange]);

  const updateField = useCallback((name, fieldValue) => {
    setDraft((prev) => {
      const next = { ...prev, [name]: fieldValue };
      evaluate(next);
      return next;
    });
  }, [evaluate]);

  // Initial evaluation so the parent's `ok` reflects required-field state
  // before the user touches anything (defaults can already be valid).
  useEffect(() => {
    evaluate(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!schema || propNames.length === 0) {
    return (
      <p className="schema-form-empty">No input required for this workflow.</p>
    );
  }

  return (
    <div className="schema-form">
      {propNames.map((name) => {
        const propSchema = properties[name];
        const isReq = required.has(name);
        const err = serverErrors[name];
        const v = draft[name];

        if (isPrimitive(propSchema) || isStringEnum(propSchema)) {
          return (
            <div key={name} className={`schema-form-field ${err ? 'has-error' : ''}`}>
              <FieldLabel name={name} schema={propSchema} required={isReq} />
              <PrimitiveField
                name={name}
                schema={propSchema}
                value={v}
                onChange={(val) => updateField(name, val)}
                required={isReq}
                error={err}
                disabled={disabled}
              />
              {err && <p className="schema-form-error">{err}</p>}
            </div>
          );
        }

        if (isStringArray(propSchema)) {
          return (
            <div key={name} className={`schema-form-field ${err ? 'has-error' : ''}`}>
              <FieldLabel name={name} schema={propSchema} required={isReq} />
              <StringArrayField
                name={name}
                schema={propSchema}
                value={v}
                onChange={(val) => updateField(name, val)}
                required={isReq}
                error={err}
                disabled={disabled}
              />
              {err && <p className="schema-form-error">{err}</p>}
            </div>
          );
        }

        if (isSimpleObject(propSchema, 0)) {
          // Nested 1-level — render as grouped fieldset of primitives.
          const nestedProps = propSchema.properties || {};
          const nestedRequired = new Set(propSchema.required || []);
          return (
            <fieldset key={name} className={`schema-form-fieldset ${err ? 'has-error' : ''}`}>
              <legend className="schema-form-legend">
                {propSchema.title || name}
                {isReq && <span className="schema-form-required" aria-label="required">*</span>}
              </legend>
              {Object.keys(nestedProps).map((nestedName) => {
                const ns = nestedProps[nestedName];
                const nReq = nestedRequired.has(nestedName);
                const nv = (v || {})[nestedName];
                const nestedKey = `${name}.${nestedName}`;
                const nestedErr = serverErrors[nestedKey];
                return (
                  <div key={nestedKey} className={`schema-form-field ${nestedErr ? 'has-error' : ''}`}>
                    <FieldLabel name={nestedKey} schema={ns} required={nReq} />
                    {isStringArray(ns) ? (
                      <StringArrayField name={nestedKey} schema={ns} value={nv}
                        onChange={(val) => updateField(name, { ...(v || {}), [nestedName]: val })}
                        required={nReq} error={nestedErr} disabled={disabled} />
                    ) : (
                      <PrimitiveField name={nestedKey} schema={ns} value={nv}
                        onChange={(val) => updateField(name, { ...(v || {}), [nestedName]: val })}
                        required={nReq} error={nestedErr} disabled={disabled} />
                    )}
                    {nestedErr && <p className="schema-form-error">{nestedErr}</p>}
                  </div>
                );
              })}
              {err && <p className="schema-form-error">{err}</p>}
            </fieldset>
          );
        }

        // Fallback — JSON textarea. Covers nested arrays, oneOf, $ref,
        // anything we don't render natively. Server validates the parsed
        // JSON against the real schema; we only check that it parses.
        return (
          <div key={name} className={`schema-form-field ${err ? 'has-error' : ''}`}>
            <FieldLabel name={name} schema={propSchema} required={isReq} />
            <JsonField name={name} value={v} onChange={(val) => updateField(name, val)} disabled={disabled} error={err} />
            <p className="schema-form-hint">Advanced — paste JSON matching this field's schema.</p>
            {err && <p className="schema-form-error">{err}</p>}
          </div>
        );
      })}
    </div>
  );
}
